import {
  EMAIL_REGEXP,
  ErrorCode,
  INVITE_ACCOUNT_KEY,
  InfoIcon,
  PasswordNotVisibleIcon,
  PasswordVisibleIcon,
  REMEMBER_ACCOUNT_KEY,
  TRIGGER_TYPES,
  UserRole,
  UserStatus,
  WarningIcon,
  firstPage,
} from '@/constants';
import useUser from '@/hooks/useUser';
import { comeFrom } from '@/pages/login';
import { loginUser } from '@/redux/slice/user';
import { login, thirdPartyLogin } from '@/services/login';
import { MyAES, safeDecodeURIComponent } from '@/utils/aes';
import { setAuthCookies } from '@/utils/cookie';
import { addGtagEvent, gaEventName } from '@/utils/ga';
import { setAuth } from '@/utils/request';
import { isString } from '@/utils/typeHelpers';
import { isEmail, rsaEncrypt } from '@/utils/util';
import { Alert, Button, Checkbox, Form, Input, message } from 'antd';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { INVITE_COOPERATION_EMAIL, INVITE_COOPERATION_ID, INVITE_KOL_EMAIL, INVITE_KOL_ID } from '../invite/constants';
import styles from './Login.module.scss';
import ThirdPartyAuthButtons, { SupportThirdPartyAuthPlatform, ThirdPartyAuthInfo } from './ThirdPartyAuthButtons';
import { Policy } from '@/layout/LoginLayout';
import { useRequest } from 'ahooks';
import { safeGetItem, safeRemoveItem, safeSetItem } from '@/utils/storage';

// 模块级常量，保证缺省时对象引用稳定，避免 refreshDeps/useEffect 反复触发
const EMPTY_QUERY: Record<string, string> = {};

export const LoginRoleList = Object.values(UserRole).filter((role) => typeof role === 'number');

const intlMessgaes = defineMessages({
  emailPlaceholder: {
    description: '请输入电子邮箱地址',
    defaultMessage: 'Please enter email',
  },
  emailRequiredError: {
    description: '请输入邮箱',
    defaultMessage: 'Please enter email',
  },
  emailInvalidError: {
    defaultMessage: 'Invalid email format',
  },
  passwordPlaceholder: {
    defaultMessage: '6-12 characters, number(s) + letter(s)',
  },
  roleDeny: { defaultMessage: 'insufficient authority' },
});

export const transferQuery = (query: Record<string, string>) => {
  const params = new URLSearchParams();
  Object.entries(query).forEach(([key, value]) => {
    if (value) params.append(key, value);
  });
  const str = params.toString();
  return str ? `?${str}` : '';
};

const Login: React.FC<{ query?: Record<string, string> }> = ({ query = EMPTY_QUERY }) => {
  const { formatMessage } = useIntl();
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const router = useRouter();
  const [emailOrPasswordError, setEmailOrPasswordError] = useState(false);
  const [noCompanyInfoError, setNoCompanyInfoError] = useState(false);
  const password = Form.useWatch('password', form);
  const { email: user_email } = useUser();
  const { utm_ydk, utm_yde, f, p } = query;
  const kol_id = safeDecodeURIComponent(utm_ydk);
  const kol_email = safeDecodeURIComponent(utm_yde);
  const from = comeFrom({ kol_id, kol_email, f, p });

  useEffect(() => {
    const { email, f } = query;
    // 在邀请页填了邮箱后跳转过来的
    if (f === 'ivth') {
      const inviteEmail = safeGetItem(INVITE_ACCOUNT_KEY, 'session');
      inviteEmail && form.setFieldsValue({ email: inviteEmail });
      inviteEmail && safeRemoveItem(INVITE_ACCOUNT_KEY, 'session');
    }
    if (isString(email)) {
      form.setFieldsValue({
        email: email,
      });
    }
  }, [query, form]);

  //#region 后台系统的 KOL 登录时重定向到 KOC 平台
  useRequest(
    async () => {
      const { redirectAuth } = query;
      if (!redirectAuth) return;

      const authInfo = JSON.parse(atob(redirectAuth)) as { email: string; password: string } | { email: string; userId: number; userType: number };

      if ('password' in authInfo) {
        await handleFinish({ ...authInfo, needEncrypt: false });
      } else {
        await handleMessage(authInfo, 'Google', false);
      }
    },
    {
      onError: (error) => {
        // 清空 query
        router.replace('/login');
      },
      refreshDeps: [query],
    },
  );
  //#endregion

  const formatError = () => {
    if (emailOrPasswordError) setEmailOrPasswordError(false);
    if (noCompanyInfoError) setNoCompanyInfoError(false);
  };

  const handleFinish = async ({
    email,
    password,
    remember,
    needEncrypt = true,
  }: {
    email: string;
    password: string;
    remember?: boolean;
    needEncrypt?: boolean;
  }) => {
    await handleLogin(login.bind(null, email, needEncrypt ? await rsaEncrypt(password) : password), email);

    addGtagEvent({
      event: gaEventName.CLICK,
      options: {
        click_id: 'login_btn_click',
        user_email,
        from,
        kol_id:
          safeGetItem(INVITE_COOPERATION_ID, 'session') ||
          safeGetItem(INVITE_KOL_ID, 'session') ||
          (Number(MyAES.decrypt(kol_id)) && MyAES.decrypt(kol_id)),
        kol_email:
          safeGetItem(INVITE_COOPERATION_EMAIL, 'session') || safeGetItem(INVITE_KOL_EMAIL, 'session') || (kol_email && MyAES.decrypt(kol_email)),
      },
    });
    if (remember) return void safeSetItem(REMEMBER_ACCOUNT_KEY, email);
    safeRemoveItem(REMEMBER_ACCOUNT_KEY);
  };

  /**
   * 登录
   * @param loginService 登录接口
   * @param email 常规登录时传入邮箱，用于登录失败时跳转到邮件激活页面
   * @returns
   */
  const handleLogin = async (loginService: () => ReturnType<typeof thirdPartyLogin>, email?: string) => {
    setLoading(true);
    try {
      const res = await loginService();
      window.clarity('identify', res.email);
      if (res.userType == undefined) return;
      const userType = Number(res.userType);
      if (userType === 0) {
        const userInfo = {
          userId: await rsaEncrypt(res.userId.toString()),
          email: await rsaEncrypt(res.email),
        };
        // 如果未注册则前往选择用户身份
        return router.replace('/register?userInfo=' + encodeURIComponent(JSON.stringify(userInfo)));
      }
      if (!LoginRoleList.includes(userType)) {
        message.error(formatMessage(intlMessgaes.roleDeny));
        return;
      }

      setAuthCookies(res);
      setAuth(res.userId, res.authorization);
      dispatch(loginUser(res));
      const isKOC_ADVERTISER = userType === UserRole.KOC_ADVERTISER;
      const invitation = safeGetItem('invitationFirstPage', 'session');

      if (res['status'] === UserStatus.MCN_AUDITING) {
        router.replace('/mcn/apply?status=pending');
        return;
      }
      if (res['userType'] === UserRole.MCN && res['status'] === UserStatus.REGISTERED) {
        router.replace('/mcn/apply');
        return;
      }

      if (isKOC_ADVERTISER && res.status === UserStatus.REGISTERED) {
        router.push('/company-info');
        return;
      }
      if (invitation) {
        router.push('/koc/orders');
        safeRemoveItem('invitationFirstPage', 'session');
        return;
      }
      router.push(firstPage[res.userType]);
    } catch (error: any) {
      if (!email) return; // 如果是第三方授权登录，不需要跳转到邮件激活页面
      const { code } = error;
      switch (code) {
        case ErrorCode.INACTIVE_KOL:
          router.push({
            pathname: '/register/validation',
            query: {
              email,
              identity: 'KOC',
              sendEmail: Date.now(),
            },
          });
          break;
        case ErrorCode.INACTIVE_ADVERTISER:
          router.push({
            pathname: '/register/validation',
            query: {
              email,
              identity: 'ADVERTISER',
              sendEmail: Date.now(),
            },
          });
          break;
        case ErrorCode.INACTIVE_MCN:
          router.push({
            pathname: '/register/validation',
            query: {
              email,
              identity: 'MCN',
              sendEmail: Date.now(),
            },
          });
          break;
        default:
          setEmailOrPasswordError(true);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGo2ResetPsw = () => {
    const email = form.getFieldValue('email');
    if (isString(email) && isEmail(email)) {
      router.push({
        pathname: '/password-reset',
        query: { email, from: '/login' },
      });
    } else {
      router.push('/password-reset');
    }
  };

  const emailOrPasswordErrorRender = emailOrPasswordError && (
    <Alert message={<FormattedMessage defaultMessage="The email and password do not match" />} type="error" showIcon icon={<WarningIcon />} />
  );

  const noCompanyInfoErrorRender = noCompanyInfoError && (
    <Alert
      message={
        <FormattedMessage
          defaultMessage="Please enter your<link> company's info </link> to activate your account."
          values={{
            link: (text: any) => <Link href="/company-info">{text}</Link>,
          }}
        />
      }
      type="info"
    />
  );

  //#region --------- 第三方授权注册 ---------
  const handleMessage = async (authInfo: ThirdPartyAuthInfo, platform: SupportThirdPartyAuthPlatform, needEncrypt = true) => {
    // 从第三方授权页面传递过来的信息
    const { userId, email, userType } = authInfo;
    if (!email) {
      // 无法获取到邮箱时报错提示（使用 facebook 授权时可能无法获取到邮箱）
      message.error({
        content: formatMessage({ defaultMessage: 'Email authentication failed' }),
        key: 'getEmailFail',
      });
      // 跳转到注册页，使用常规注册流程；如果 f 为 mbox 则默认 KOL 身份
      router.replace(`/register${transferQuery({ f })}`);
    } else {
      // email 未加密时提示
      needEncrypt &&
        message.info({
          content: formatMessage({ defaultMessage: 'Authenticated as {email}' }, { email }),
          icon: <InfoIcon />,
          key: 'authenticated',
        });
      addGtagEvent({
        event: gaEventName.THIRD_PARTY_AUTH_SUCCESS,
        options: {
          from,
          email,
          user_id: userId,
          platform,
          page_location: window.location.href,
          page_title: '登录页',
        },
      });
      window.clarity('identify', email);

      const userInfo = {
        userId: needEncrypt ? await rsaEncrypt(userId.toString()) : userId.toString(),
        email: needEncrypt ? await rsaEncrypt(email) : email,
      };

      if (userType === 0) {
        // 如果未注册则前往选择用户身份，如果 f 为 mbox 则直接注册登录，默认 KOL 身份
        router.replace(`/register${transferQuery({ userInfo: encodeURIComponent(JSON.stringify(userInfo)), f })}`);
      } else {
        // 如果已注册则直接登录
        handleLogin(thirdPartyLogin.bind(null, userInfo));
      }
    }
  };
  //#endregion

  return (
    <div className={styles.container}>
      <div className={styles.title}>
        <FormattedMessage defaultMessage="Log in" />
      </div>
      <div className={styles.register}>
        <FormattedMessage
          defaultMessage="No account? <link>Sign up</link>."
          values={{
            link: (text: string | React.ReactNode) => (
              <Link key={'register-link'} href={`/register${transferQuery(query)}`}>
                {text}
              </Link>
            ),
          }}
        />
      </div>

      <ThirdPartyAuthButtons handleMessage={handleMessage} />

      <Form
        className={styles.form}
        form={form}
        name="login"
        layout="vertical"
        initialValues={{
          remember: false,
          email: safeGetItem(REMEMBER_ACCOUNT_KEY),
        }}
        onFinish={handleFinish}
        requiredMark={false}
      >
        <Form.Item
          name="email"
          label={<FormattedMessage defaultMessage="Email" />}
          validateFirst
          rules={[
            {
              required: true,
              message: formatMessage(intlMessgaes.emailRequiredError),
              validateTrigger: TRIGGER_TYPES.onBlur,
            },
            {
              pattern: EMAIL_REGEXP,
              message: formatMessage(intlMessgaes.emailInvalidError),
              validateTrigger: TRIGGER_TYPES.onSubmit,
            },
          ]}
          validateTrigger={[TRIGGER_TYPES.onSubmit, TRIGGER_TYPES.onBlur]}
        >
          <Input allowClear placeholder={formatMessage(intlMessgaes.emailPlaceholder)} onChange={formatError} />
        </Form.Item>

        <Form.Item
          name="password"
          label={
            <div className={styles.passwordLabel}>
              <span>
                <FormattedMessage defaultMessage="Password" />
              </span>
              <a onClick={handleGo2ResetPsw}>
                <FormattedMessage defaultMessage="Forget password" />
              </a>
            </div>
          }
          rules={[
            {
              required: true,
              message: formatMessage(intlMessgaes.emailRequiredError),
              validateTrigger: 'onSubmit',
            },
          ]}
        >
          <Input.Password
            allowClear
            onChange={formatError}
            placeholder={formatMessage(intlMessgaes.passwordPlaceholder)}
            iconRender={(visible) => (
              <div style={{ display: 'flex' }}>
                {!visible ? (
                  <span className={styles.passwordIcon}>
                    <PasswordNotVisibleIcon />
                  </span>
                ) : (
                  <span className={styles.passwordIcon}>
                    <PasswordVisibleIcon />
                  </span>
                )}
              </div>
            )}
          />
        </Form.Item>

        <Form.Item className={styles.rememberFormItem} name="remember" valuePropName="checked">
          <Checkbox className={styles.remember}>
            <span className="select-none">
              <FormattedMessage defaultMessage="Remember password" />
            </span>
          </Checkbox>
        </Form.Item>

        {emailOrPasswordErrorRender}
        {noCompanyInfoErrorRender}

        <Button type="primary" htmlType="submit" className={styles.loginBtn} loading={loading} disabled={!password}>
          <FormattedMessage defaultMessage="Log in" />
        </Button>
      </Form>

      <Policy />
    </div>
  );
};

export default Login;
